import { useEffect, useRef, useState } from 'react'

import { captureException } from '@sentry/nextjs'

import {
  useFacilityNamesLazyQuery,
  useFloorsByFacilityIdsLazyQuery,
} from '@/graphql/generated/hooks'
import { FacilityType } from '@/graphql/generated/schemas'

import { parseDeviceCSVFile } from '../utils/csvParser'
import { CSVParseResultI, validateDevices } from '../utils/csvValidator'
import { isValidFileType } from '../utils/utils'

interface UseDeviceCSVProcessorReturnI {
  isLoading: boolean
}

export const useDeviceCSVProcessor = (
  fileUpload: FileList | null,
  onParseResultChange: (result: CSVParseResultI | null) => void
): UseDeviceCSVProcessorReturnI => {
  const [isLoading, setIsLoading] = useState(false)
  const [fetchFacilities] = useFacilityNamesLazyQuery()
  const [fetchFloors] = useFloorsByFacilityIdsLazyQuery()
  const onParseResultChangeRef = useRef(onParseResultChange)
  onParseResultChangeRef.current = onParseResultChange

  useEffect(() => {
    const parseFile = async () => {
      if (!fileUpload || fileUpload.length === 0) {
        onParseResultChangeRef.current(null)
        return
      }

      if (!isValidFileType(fileUpload)) {
        onParseResultChangeRef.current({
          data: [],
          errors: ['Invalid MIME type. MIME type must be text/csv.'],
        })
        captureException(
          Error(
            `Invalid MIME Type. Expected: text/csv Got: ${fileUpload?.[0]?.type}`
          )
        )
        return
      }

      setIsLoading(true)
      try {
        // Step 1: Parse CSV only, no validation
        const rawResult = await parseDeviceCSVFile(fileUpload[0])
        if (rawResult.errors.length > 0) {
          onParseResultChangeRef.current({ data: [], errors: rawResult.errors })
          return
        }

        // Step 2: Fetch facilities by exact name match
        const uniqueFacilityNames = [
          ...new Set(
            rawResult.rows.map(
              (r) => r.fields[rawResult.headerIndex['facility_name']] ?? ''
            )
          ),
        ].filter(Boolean)

        const facilitiesResult = await fetchFacilities({
          variables: {
            filter: { type: FacilityType.Normal, nameIn: uniqueFacilityNames },
          },
        })

        const facilityNameToIdMap = new Map<string, string>()
        facilitiesResult.data?.facilityNames?.edges?.forEach((edge) => {
          if (edge?.node) facilityNameToIdMap.set(edge.node.name, edge.node.id)
        })

        // Step 3: Fetch all floors for those facilities in one request
        const facilityIds = [...facilityNameToIdMap.values()]
        const floorLookup = new Map<string, string>()
        if (facilityIds.length > 0) {
          const floorsResult = await fetchFloors({
            variables: { facilityIds },
          })
          floorsResult.data?.floors?.edges?.forEach((edge) => {
            if (edge?.node) {
              const facilityId = edge.node.facility?.id
              const floorName = edge.node.name
              const floorId = edge.node.id
              if (facilityId && floorName && floorId) {
                floorLookup.set(`${facilityId}::${floorName}`, floorId)
              }
            }
          })
        }

        // Step 4: Run all validations (field-level + facility/floor)
        const result = validateDevices(
          rawResult,
          facilityNameToIdMap,
          floorLookup
        )
        onParseResultChangeRef.current(result)
      } catch (error) {
        onParseResultChangeRef.current({
          data: [],
          errors: ['Failed to parse CSV file'],
        })
        captureException(error)
      } finally {
        setIsLoading(false)
      }
    }

    parseFile()
  }, [fetchFacilities, fetchFloors, fileUpload])

  return { isLoading }
}
